import {useLayoutEffect, useRef, useState} from 'react';
import {useStore} from 'jotai';
import {useQueryClient} from '@tanstack/react-query';
import {useSearchParams} from 'react-router-dom';
import {usePersonalScope} from '@api/workspaces';
import * as atoms from '@lib/atoms';
import {callsFilterAtom} from '@features/filter-calls';
import {tasksFilterAtom} from '@features/manage-tasks';
import {meetingsFilterAtom} from '@features/filter-meetings';

const SCOPED_QUERIES = new Set([
  'dashboard',
  'queues',
  'feed',
  'communication',
  'communications',
  'communications-list',
  'employee',
  'employees',
  'workspace-rating',
  'tasks',
  'analytics-insights',
  'notifications',
  'queue-resolutions',
  'meetings-detail',
  'meetings-media',
]);

/** Сбрасывает старый UI и QueryClient при смене actor/workspace/access, до монтирования экранов. */
export const useWorkspaceUiScope = () => {
  const scope = usePersonalScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastReady = useRef<{
    actor: string | null;
    workspace: string | null;
    signature: string;
  } | null>(null);
  const store = useStore();
  const queryClient = useQueryClient();
  const previous = useRef<string | null>(null);
  const [prepared, setPrepared] = useState<string | null>(null);
  useLayoutEffect(() => {
    const changed =
      previous.current !== null && previous.current !== scope.signature;
    previous.current = scope.signature;
    if (changed) {
      const filters = {
        predicate: (q: {queryKey: readonly unknown[]}) =>
          SCOPED_QUERIES.has(String(q.queryKey[0])) ||
          (q.queryKey[0] === 'workspaces' &&
            (q.queryKey[2] === 'members' ||
              q.queryKey[2] === 'grants' ||
              (q.queryKey[1] === 'mine' && q.queryKey[2] !== scope.actorSid) ||
              (q.queryKey[2] === 'access' &&
                q.queryKey[4] !== scope.actorSid))),
      };
      void queryClient.cancelQueries(filters);
      queryClient.removeQueries(filters);
      for (const atom of [
        atoms.dashCallOpenAtom,
        atoms.callDetailOpenAtom,
        atoms.managerDetailOpenAtom,
        atoms.taskDetailOpenAtom,
        atoms.taskCreateOpenAtom,
        atoms.meetingDetailOpenAtom,
        atoms.notificationSettingsOpenAtom,
      ])
        store.set(atom, false);
      for (const atom of [
        atoms.dashCallIdAtom,
        atoms.selectedCallIdAtom,
        atoms.selectedManagerIdAtom,
        atoms.selectedTaskIdAtom,
        atoms.meetingDetailIdAtom,
      ])
        store.set(atom, null);
      store.set(atoms.taskCreatePrefillAtom, null);
      store.set(atoms.dashCallContextAtom, null);
      store.set(atoms.dashQueueTabAtom, 'urgent');
      store.set(atoms.analyticsChannelFilterAtom, 'all');
      store.set(atoms.resolvedQueueIdsAtom, new Set<string>());
      store.set(atoms.aiContextAtom, null);
      store.set(atoms.aiMessagesAtom, []);
      store.set(callsFilterAtom, {
        search: '',
        isTargeted: true,
        searchField: 'communicationId',
      });
      store.set(tasksFilterAtom, {search: ''});
      store.set(meetingsFilterAtom, {
        search: '',
        type: 'all',
        searchField: 'communicationId',
      });
    }
    if (scope.isReady) {
      const old = lastReady.current;
      if (old && old.signature !== scope.signature) {
        const actorChanged = old.actor !== scope.actorSid;
        const workspaceChanged = old.workspace !== scope.workspaceSid;
        const targetedLink =
          workspaceChanged &&
          searchParams.get('workspaceId') === scope.workspaceSid;
        if (actorChanged || !targetedLink) {
          const next = new URLSearchParams(searchParams);
          for (const key of ['taskId', 'id', 'insightSid']) next.delete(key);
          if (actorChanged) {
            next.delete('notificationSid');
            next.delete('queue');
          }
          if (searchParams.has('taskId')) next.delete('workspaceId');
          if (next.toString() !== searchParams.toString())
            setSearchParams(next, {replace: true});
        }
      }
      lastReady.current = {
        actor: scope.actorSid,
        workspace: scope.workspaceSid,
        signature: scope.signature,
      };
    }
    setPrepared(scope.signature);
  }, [
    scope.signature,
    scope.isReady,
    scope.actorSid,
    scope.workspaceSid,
    store,
    queryClient,
    searchParams,
    setSearchParams,
  ]);
  return {...scope, canRender: scope.isReady && prepared === scope.signature};
};
